<template>
    <Modal :title="$t('labels.edit_products_modal')" ref="modal" size="lg">
        <div class="modal-body">
            <div class="row mb-3" v-if="allChecked && selectedProductItems.length < totalProducts">
                <div class="col-lg-6">
                    <InputGroup>
                        <Radio :model-value="editOption === 'selected'" @update:model-value="editOption = 'selected'" />
                        <template #append>
                            {{ $t('labels.edit_only_selected_products', {total: selectedProductItems.length}) }}
                        </template>
                    </InputGroup>
                </div>
                <div class="col-lg-6">
                    <InputGroup>
                        <Radio :model-value="editOption === 'all'" @update:model-value="editOption = 'all'" />
                        <template #append>
                            {{ $t('labels.edit_all_products', {total: totalProducts}) }}
                        </template>
                    </InputGroup>
                </div>
            </div>
            <div class="table-responsive" v-if="!task.state.isRunning">
                <table class="table table-bordered table-fixed">
                    <thead>
                        <tr>
                            <th width="50%">{{ $t('labels.field') }}</th>
                            <th width="50%">{{ $t('labels.action') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(field, index) in fields" :key="field.name">
                            <tr>
                                <td>{{ field.label }}</td>
                                <td>
                                    <AppSelect
                                        option-key="key"
                                        option-value="value"
                                        :model-value="field.selectedAction?.key"
                                        :selected-value="field.selectedAction?.value"
                                        :options="getFieldActions(field)"
                                        @update:model-value="selectAction($event, index)"
                                        can-be-empty
                                    />
                                </td>
                            </tr>
                            <tr v-if="field.selectedAction?.key === 'change'">
                                <td colspan="2">
                                    <Autocomplete
                                        v-if="field.type === 'category_autocomplete'"
                                        :url="`/projects/${$route.params.projectUuid}/pim/types`"
                                        :selected-value="field.value?.name"
                                        :model-value="field.value?.uuid"
                                        @update:model-value="newValue => field.value = newValue"
                                    />
                                    <div v-else-if="field.type === 'number'" class="d-flex" style="gap: 0.5rem">
                                        <AppSelect
                                            v-if="field.name === 'cost'"
                                            :model-value="field.currency?.uuid"
                                            :selected-value="field.currency?.name"
                                            :options="currencies"
                                            @update:model-value="field.currency = $event"
                                            option-key="uuid"
                                            option-value="name"
                                        />
                                        <Input
                                            type="number"
                                            v-model="field.value"
                                            :disabled="field.name === 'cost' && !field.currency"
                                        />
                                    </div>
                                    <Input
                                        v-else-if="field.type === 'custom_field_string' || field.type === 'custom_field_link'"
                                        v-model="field.value"
                                    />
                                    <Input
                                        v-else-if="field.type === 'custom_field_number'"
                                        type="number"
                                        v-model="field.value"
                                    />
                                    <textarea
                                        v-else-if="field.type === 'custom_field_longtext'"
                                        class="form-control"
                                        v-model="field.value"
                                    />
                                    <AppSelect
                                        v-else-if="field.type === 'custom_field_select'"
                                        :model-value="field.value"
                                        :selected-value="field.value"
                                        :options="getCustomFieldOptions(field.customField)"
                                        option-key="key"
                                        option-value="value"
                                        @update:model-value="field.value = $event?.key"
                                    />
                                    <Checkbox
                                        v-else-if="field.type === 'custom_field_checkbox'"
                                        v-model="field.value"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
            <TaskProgressBar v-else :done="task.state.done" :total="task.state.total"/>
        </div>
        <div class="card-footer d-flex justify-content-end" v-if="!task.state.isRunning">
            <Button size="sm" color="teal" @click="save">
                <span>{{ $t('buttons.save') }}</span>
            </Button>
        </div>
    </Modal>
</template>
<script>
import Modal from "../../../../../components/new/modal/AppModal.vue";
import Autocomplete from "../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import AppSelect from "../../../../../components/new/form/select/AppSelect.vue";
import Input from "../../../../../components/new/form/Input.vue";
import Checkbox from "../../../../../components/new/form/checkbox/AppCheckbox.vue";
import Button from "../../../../../components/new/button/AppButton.vue";
import TaskProgressBar from "../../../../../components/new/progress/TaskProgressBar.vue";
import Radio from "../../../../../components/new/form/radio/Radio.vue";
import InputGroup from "../../../../../components/new/form/InputGroup.vue";
import { httpClient } from "../../../../../services/HttpClient";
import { createProgressTask } from "../../../../../services/Progress/ProgressTask";
import { chunk } from "../../../../../utils";

export default {
    name: "EditInternalProductsModal",
    components: {
        Modal, Autocomplete, AppSelect, Input, Checkbox, Button, TaskProgressBar, Radio, InputGroup
    },
    props: {
        selectedProductItems: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            task: createProgressTask(),
            fields: [],
            currencies: [],
            customFields: [],
            allChecked: false,
            totalProducts: 0,
            editOption: 'selected',
            tableQueryParams: { columns: [] },
            fieldActions: [
                { key: 'change', value: this.$t('buttons.change') },
                { key: 'clear', value: this.$t('buttons.clear') }
            ]
        }
    },
    methods: {
        async show(tableQueryParams, allChecked = false, totalProducts = 0) {
            this.tableQueryParams = JSON.parse(JSON.stringify(tableQueryParams));
            this.allChecked = allChecked;
            this.totalProducts = totalProducts;
            await Promise.all([
                this.fetchCurrencies(),
                this.fetchCustomFields()
            ]);
            this.initializeFields();
            this.$refs.modal.show();
        },
        async fetchAllProductItemUuids() {
            const queryParams = {
                ...this.tableQueryParams,
                columns: [
                    ...this.tableQueryParams.columns.filter(column => column.searchable),
                    {
                        name: 'uuid',
                        orderable: 0,
                        searchable: 0
                    }
                ]
            };

            const firstResponse = await httpClient.get('/catalog/product-items/table', {
                ...queryParams,
                limit: 1
            });
            const total = firstResponse.data.meta.total;
            const pages = Math.ceil(total / 50);

            const responses = await Promise.all(
                Array.from({ length: pages }, (_, i) =>
                    httpClient.get('/catalog/product-items/table', {
                        ...queryParams,
                        limit: 50,
                        page: i + 1
                    })
                )
            );

            return responses.flatMap(response => response.data.data.map(item => item.uuid));
        },
        fetchCurrencies() {
            return httpClient.get('/list/currencies', { limit: 100 }).then((response) => {
                this.currencies = response.data.data;
            });
        },
        fetchCustomFields() {
            return httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/custom-fields`, {
                models: ['product-item'],
                limit: 0
            }).then((response) => {
                this.customFields = response.data.data;
            });
        },
        hide() {
            this.task.cancel();
            this.$refs.modal.hide();
            this.fields = [];
            this.customFields = [];
            this.allChecked = false;
            this.totalProducts = 0;
            this.editOption = 'selected';
            this.tableQueryParams = { columns: [] };
        },
        initializeFields() {
            const fields = [
                {
                    name: 'category_uuid',
                    label: this.$t('labels.category'),
                    type: 'category_autocomplete',
                    selectedAction: null,
                    value: null
                },
                {
                    name: 'cost',
                    label: this.$t('labels.cost'),
                    type: 'number',
                    selectedAction: null,
                    value: null,
                    currency: null
                }
            ];

            for (const customField of this.customFields) {
                fields.push({
                    name: `custom_fields.${customField.uuid}`,
                    label: customField.title,
                    type: `custom_field_${customField.type}`,
                    customField: customField,
                    selectedAction: null,
                    value: null
                });
            }

            this.fields = fields;
        },
        getFieldActions(field) {
            if (field.name.startsWith('custom_fields.')) {
                return this.fieldActions.filter(action => action.key !== 'clear');
            }
            return this.fieldActions;
        },
        getCustomFieldOptions(customField) {
            return customField.additional.items.map(item => ({ key: item, value: item }));
        },
        selectAction(action, index) {
            this.fields[index].selectedAction = action;
            this.fields[index].value = null;
            if ('currency' in this.fields[index]) {
                this.fields[index].currency = null;
            }
        },
        buildUpdateRequest() {
            const updateRequest = {};
            const customFieldUpdates = [];

            for (const field of this.fields) {
                if (!field.selectedAction) {
                    continue;
                }

                if (field.name.startsWith('custom_fields.')) {
                    const uuid = field.name.slice('custom_fields.'.length);
                    if (field.selectedAction.key === 'change') {
                        if (field.value === null || field.value === '') {
                            continue;
                        }
                        customFieldUpdates.push({ uuid, value: field.value });
                    } else if (field.selectedAction.key === 'clear') {
                        customFieldUpdates.push({ uuid, value: null });
                    }
                    continue;
                }

                if (field.selectedAction.key === 'change') {
                    if (field.type === 'category_autocomplete') {
                        updateRequest[field.name] = field.value ? field.value.uuid : null;
                    } else {
                        updateRequest[field.name] = field.value;
                        if (field.name === 'cost' && field.currency) {
                            updateRequest.cost_currency_uuid = field.currency.uuid;
                        }
                    }
                } else if (field.selectedAction.key === 'clear') {
                    updateRequest[field.name] = null;
                }
            }

            if (customFieldUpdates.length > 0) {
                updateRequest.custom_fields = customFieldUpdates;
            }

            return updateRequest;
        },
        async save() {
            const updateRequest = this.buildUpdateRequest();

            if (Object.keys(updateRequest).length === 0) {
                return;
            }

            const completed = await this.task.run(async ({setTotal, tick}) => {
                const uuids = this.editOption === 'all'
                    ? await this.fetchAllProductItemUuids()
                    : this.selectedProductItems;

                const batches = chunk(uuids, 10);

                setTotal(batches.length);

                await Promise.all(batches.map(async (products) => {
                    await httpClient.post('/batch', {
                        data: products.map((uuid) => ({
                            method: 'PATCH',
                            path: `/catalog/product-items/${uuid}`,
                            body: updateRequest
                        }))
                    });

                    tick();
                }));
            });

            if (!completed) {
                return;
            }

            this.$emit('update:table');
            this.hide();
        }
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>
